import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  SwimmingPool,
  Fire,
  Mountains,
  WifiHigh,
  ForkKnife,
  CarSimple,
  Thermometer,
  Coffee,
  Tree,
  ArrowUpRight,
  List as MenuIcon,
  X,
  CalendarBlank,
  UsersThree,
  MapPin,
  InstagramLogo,
  FacebookLogo,
  Phone,
  EnvelopeSimple,
  Quotes,
  Snowflake,
  Path,
  Campfire,
} from "@phosphor-icons/react";
import { translations } from "./i18n";
import DateRangePicker from "./DateRangePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_URL = "https://www.paypal.com/ncp/payment/4A5LEYXGWSYGU";

const MEDIA = {
  hero: "https://customer-assets.emergentagent.com/job_site-builder-2662/artifacts/m6b0ajqj_0-02-05-b0689a241d2a98ce68d447789475d7dd97502fc02a0cd24f5ae88bb87a7cf176_8cee93a7dfaa0e8b.jpg",
  fireplace: "https://customer-assets.emergentagent.com/job_site-builder-2662/artifacts/ydo2ekcs_0-02-05-7d379309bedc152e14fba3a132904195a343dd3c29438c975b1b75ea7d5e3da4_71e05fc0bb189158.jpg",
  exteriorNight: "https://customer-assets.emergentagent.com/job_site-builder-2662/artifacts/m6b0ajqj_0-02-05-b0689a241d2a98ce68d447789475d7dd97502fc02a0cd24f5ae88bb87a7cf176_8cee93a7dfaa0e8b.jpg",
  bedroom1: "https://customer-assets.emergentagent.com/job_site-builder-2662/artifacts/f2c1e9lg_0-02-05-0c63518269f2f3b4eb184ea6525effa8327a126f5037e4b135d6520f8f447979_585cc04a60252e94.jpg",
  bedroom2: "https://customer-assets.emergentagent.com/job_site-builder-2662/artifacts/97km0vau_0-02-05-1587feb4205779ae46b084e60accccafb6c63870f891c357d42d709145f01ff9_2d252e73079c26a4.jpg",
  SwimmingPool: "https://villadelightks.com/images/006.jpg",
  snow: "https://customer-assets.emergentagent.com/job_site-builder-2662/artifacts/0hd5j5eq_0-02-05-4b480ca7fb593586ba7dec4a8327f2734022cd5af6952c23c2486bc87771d1ed_bcbe9d4ab913fa77.jpg",
  kitchen: "https://customer-assets.emergentagent.com/job_site-builder-2662/artifacts/6tzjab1o_0-02-05-5b0eb7e27e3a8620d993fb51c462b55c69bde8fda4bef762360abad5c73a4bbf_5c662c4cf82d03dc.jpg",
  dining: "https://customer-assets.emergentagent.com/job_site-builder-2662/artifacts/j598udne_0-02-05-bc9d56a8ed9b7b61ea908a8acaf77a04744ddad1b217e2b96e9ca63293eed4ab_721efd75278c58ea.jpg",
  lounge: "https://customer-assets.emergentagent.com/job_site-builder-2662/artifacts/cnz16wst_0-02-05-d296e7d92122bd022966ad2a14913e6df32a066e7763afe16370be44c0329500_2234dfbb7cb389f9.jpg",
  entry: "https://customer-assets.emergentagent.com/job_site-builder-2662/artifacts/oij1nkdo_0-02-05-8e1393caeee30456bc6e30988ac309308058ed0152fcac830d6cf0509b84fe0b_c8c9e28f8f048779.jpg",
  foto0: "https://villadelightks.com/images/000.jpg",
  foto1: "https://villadelightks.com/images/001.jpg",
  foto2: "https://villadelightks.com/images/002.jpg",
  foto3: "https://villadelightks.com/images/003.jpg",
  foto4: "https://villadelightks.com/images/004.jpg",
  foto5: "https://villadelightks.com/images/005.jpg",
  foto6: "https://villadelightks.com/images/006.jpg",
  foto7: "https://villadelightks.com/images/007.jpg",
  foto8: "https://villadelightks.com/images/008.jpg",
  foto9: "https://villadelightks.com/images/009.jpg",
  foto10: "https://villadelightks.com/images/002-0.jpg",
  foto11: "https://villadelightks.com/images/002-1.jpg",

};

const amenityIcons = [SwimmingPool, Fire, ForkKnife, Campfire, Mountains, WifiHigh, CarSimple, Thermometer, Snowflake];
const activityIcons = [Path, Snowflake, Tree, Campfire];

// ---------------- HEADER ----------------
const Header = ({ lang, setLang, onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const t = translations[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navs = [
    { k: "villa", href: "#about" },
    { k: "gallery", href: "#gallery" },
    { k: "rooms", href: "#rooms" },
    { k: "activities", href: "#activities" },
    { k: "contact", href: "#contact" },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 glass-header ${scrolled ? "scrolled" : ""}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <a href="#home" className="flex items-center gap-2" data-testid="logo-link">
          <span className="font-display text-2xl md:text-3xl tracking-tight" style={{ color: "var(--color-primary)" }}>
            Villa<span style={{ color: "var(--color-secondary)" }}>·</span>Delight
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
          {navs.map((n) => (
            <a key={n.k} href={n.href} className="nav-link" data-testid={`nav-${n.k}`}>
              {t.nav[n.k]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="lang-pill" data-testid="lang-toggle">
            <button
              className={lang === "AL" ? "active" : ""}
              onClick={() => setLang("AL")}
              data-testid="lang-en"
            >
              AL
            </button>
            <button
              className={lang === "EN" ? "active" : ""}
              onClick={() => setLang("EN")}
              data-testid="lang-al"
            >
              EN
            </button>
          </div>
          <div className="header-book-desktop">
            <button onClick={onBook} className="btn-primary" data-testid="header-book-btn">
              {t.nav.book}
              <ArrowUpRight size={16} weight="regular" />
            </button>
          </div>
          <button
            className="header-menu-mobile p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <MenuIcon size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden"
            style={{ background: "rgba(249, 246, 240, 0.98)", borderTop: "1px solid var(--color-border)" }}
            data-testid="mobile-menu"
          >
            <div className="flex flex-col p-6 gap-5">
              {navs.map((n) => (
                <a
                  key={n.k}
                  href={n.href}
                  className="nav-link"
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.k}`}
                >
                  {t.nav[n.k]}
                </a>
              ))}
              <button onClick={() => { setOpen(false); onBook(); }} className="btn-primary justify-center" data-testid="mobile-book-btn">
                {t.nav.book}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// ---------------- HERO ----------------
const Hero = ({ lang, formData, setFormData, onContinue }) => {
  const t = translations[lang].hero;
  const tb = translations[lang].booking;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const update = (k, v) => setFormData((prev) => ({ ...prev, [k]: v }));

  return (
    <section id="home" ref={ref} className="relative min-h-[100vh] flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div
        className="absolute inset-0 grain"
        style={{ y, scale }}
      >
        <img
          src={MEDIA.hero}
          alt="Villa Delight exterior at dusk"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.7) 100%)" }} />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pb-32 md:pb-40 pt-32">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="overline hero-text-shadow"
          style={{ color: "#E2DFD8" }}
          data-testid="hero-eyebrow"
        >
          {t.eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
          className="font-display text-white hero-text-shadow mt-5"
          style={{ fontSize: "clamp(3rem, 8vw, 7.5rem)", lineHeight: 0.92, fontWeight: 300 }}
          data-testid="hero-title"
        >
          {t.title1}
          <br />
          <em className="font-light italic" style={{ color: "#E5C9A8" }}>{t.title2}</em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="mt-8 max-w-xl text-white/90 hero-text-shadow text-lg leading-relaxed"
          data-testid="hero-desc"
        >
          {t.desc}
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a href="#about" className="btn-primary" data-testid="hero-explore-btn">
            {t.explore}
            <ArrowUpRight size={16} />
          </a>
          <a href="#contact" className="btn-primary btn-chestnut" data-testid="hero-book-btn">
            {t.book}
          </a>

<a href={PAYPAL_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-paypal" data-testid="hero-pay-btn">
  {t.pay}
  <ArrowUpRight size={16} />
</a>

        </motion.div>
      </div>

      {/* Floating booking bar */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.1 }}
        className="absolute left-1/2 -translate-x-1/2 w-[92%] max-w-5xl booking-bar z-10"
        style={{ bottom: "-48px" }}
        data-testid="booking-bar"
      >
        <div className="grid grid-cols-1 md:grid-cols-4">
          <div className="booking-field">
            <label>
              <CalendarBlank size={12} className="inline mr-1" weight="bold" /> {tb.checkin}
            </label>
            <input
              type="date"
              value={formData.check_in}
              onChange={(e) => update("check_in", e.target.value)}
              data-testid="booking-checkin"
            />
          </div>
          <div className="booking-field">
            <label>
              <CalendarBlank size={12} className="inline mr-1" weight="bold" /> {tb.checkout}
            </label>
            <input
              type="date"
              value={formData.check_out}
              onChange={(e) => update("check_out", e.target.value)}
              data-testid="booking-checkout"
            />
          </div>
          <div className="booking-field">
            <label>
              <UsersThree size={12} className="inline mr-1" weight="bold" /> {tb.guests}
            </label>
            <select
              value={formData.guests}
              onChange={(e) => update("guests", parseInt(e.target.value))}
              data-testid="booking-guests"
            >
              {[1,2,3,4,5,6,8,10,12].map((n) => (
                <option key={n} value={n}>{tb.adults(n)}</option>
              ))}
            </select>
          </div>
          <button
            className="btn-primary h-full justify-center"
            style={{ borderRadius: 0, padding: "1.25rem 1.5rem" }}
            onClick={onContinue}
            data-testid="booking-search-btn"
          >
            {tb.search}
            <ArrowUpRight size={16} />
          </button>
        </div>
      </motion.div>
    </section>
  );
};

// ---------------- MARQUEE STRIP ----------------
const MarqueeStrip = () => {
  const words = ["Villa Delight për rezervime", "Contact Viber & WhatsApp +38346323231", "Ferizaj - Jezerc"];
  return (
    <div className="py-20 border-y" style={{ borderColor: "var(--color-border)", background: "var(--color-bg)" }} data-testid="marquee-strip">
      <Marquee speed={40} gradient={false}>
        {words.map((w, i) => (
          <span key={i} className="marquee-item">
            {w}
            <span className="marquee-dot">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
};

// ---------------- ABOUT ----------------
const About = ({ lang }) => {
  const t = translations[lang].about;
  return (
    <section id="about" className="py-28 md:py-40" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1 }}
            className="md:col-span-7"
          >
            <div className="section-eyebrow">
              <span className="overline">{t.eyebrow}</span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02]" style={{ color: "var(--color-primary)" }} data-testid="about-title">
              {t.title}
            </h2>
            <div className="mt-10 space-y-6 max-w-xl" style={{ color: "var(--color-muted)" }}>
              <p className="text-lg leading-relaxed">{t.p1}</p>
              <p className="text-lg leading-relaxed">{t.p2}</p>
              <p className="text-lg leading-relaxed italic font-display" style={{ color: "var(--color-primary)", fontSize: "1.5rem", lineHeight: 1.4 }}>
                {t.p3}
              </p>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-8 pt-10 border-t" style={{ borderColor: "var(--color-border)" }}>
              {[[t.stat1, t.stat1l], [t.stat2, t.stat2l], [t.stat3, t.stat3l]].map((s, i) => (
                <div key={i} data-testid={`stat-${i}`}>
                  <div className="font-display text-4xl md:text-5xl" style={{ color: "var(--color-primary)" }}>
                    {s[0]}
                  </div>
                  <div className="overline mt-2" style={{ color: "var(--color-muted)" }}>{s[1]}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, delay: 0.2 }}
            className="md:col-span-5 md:sticky md:top-28"
          >
            <div className="gallery-img aspect-[3/4] w-full">
              <img src={MEDIA.fireplace} alt="Fireplace" className="w-full h-full object-cover" />
            </div>
            <div className="mt-4 flex items-center justify-between" style={{ color: "var(--color-muted)" }}>
              <span className="overline">Living Room · No. 01</span>
              <span className="text-xs">{lang === "EN" ? "Always burning." : "Gjithmonë e ndezur."}</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ---------------- AMENITIES ----------------
const Amenities = ({ lang }) => {
  const t = translations[lang].amenities;
  return (
    <section className="py-24 md:py-32" style={{ background: "var(--color-surface)" }} data-testid="amenities-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="section-eyebrow">
              <span className="overline">{t.eyebrow}</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl" style={{ color: "var(--color-primary)" }} data-testid="amenities-title">
              {t.title}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {t.items.map((item, i) => {
            const Icon = amenityIcons[i] || Fire;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
                className="amenity-tile"
                data-testid={`amenity-${i}`}
              >
                <Icon size={34} weight="duotone" color="#A35D43" />
                <h3 className="font-display text-2xl mt-6" style={{ color: "var(--color-primary)" }}>
                  {item.t}
                </h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--color-muted)" }}>
                  {item.d}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ---------------- GALLERY ----------------
const Gallery = ({ lang }) => {
  const t = translations[lang].gallery;
  const items = [
    { src: MEDIA.exteriorNight, h: "tall", cap: "Exterior · Dusk" },
    { src: MEDIA.snow, h: "wide", cap: "Winter · Sharr" },
    { src: MEDIA.bedroom1, h: "short", cap: "Bedroom No. 02" },
    { src: MEDIA.bedroom2, h: "short", cap: "Bedroom No. 03" },
    { src: MEDIA.foto1, h: "tall", cap: "Villa Delight1 · 01" },
    { src: MEDIA.foto2, h: "short", cap: "Villa Delight · 02" },
    { src: MEDIA.foto3, h: "short", cap: "Villa Delight · 03" },
    { src: MEDIA.foto4, h: "wide", cap: "Villa Delight · 04" },
    { src: MEDIA.foto5, h: "wide", cap: "Villa Delight · 05" },
    { src: MEDIA.foto0, h: "wide", cap: "Villa Delight · 00" },
    { src: MEDIA.foto6, h: "wide", cap: "Pishina Villa Delight · 06" },
    { src: MEDIA.foto7, h: "wide", cap: "Pishina Villa Delight · 07" },
    { src: MEDIA.foto8, h: "wide", cap: "Pishina Villa Delight · 08" },
    { src: MEDIA.foto9, h: "wide", cap: "Pishina Villa Delight · 09" },
    { src: MEDIA.foto10, h: "wide", cap: "Pishina Villa Delight · 10" },
    { src: MEDIA.foto11, h: "wide", cap: "Pishina Villa Delight · 11" },

  ];
  return (
    <section id="gallery" className="py-24 md:py-32" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="section-eyebrow">
          <span className="overline">{t.eyebrow}</span>
        </div>
        <h2 className="font-display text-4xl md:text-5xl mb-14 max-w-xl" style={{ color: "var(--color-primary)" }} data-testid="gallery-title">
          {t.title}
        </h2>
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="col-span-6 md:col-span-6 gallery-img"
            style={{ aspectRatio: "16/10" }}
            data-testid="gallery-0"
          >
            <img src={items[0].src} alt={items[0].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="col-span-6 md:col-span-6 gallery-img"
            style={{ aspectRatio: "16/10" }}
            data-testid="gallery-1"
          >
            <img src={items[1].src} alt={items[1].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-4 gallery-img"
            style={{ aspectRatio: "4/5" }}
            data-testid="gallery-2"
          >
            <img src={items[2].src} alt={items[2].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-4 gallery-img"
            style={{ aspectRatio: "4/5" }}
            data-testid="gallery-3"
          >
            <img src={items[3].src} alt={items[3].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="col-span-12 md:col-span-4 gallery-img"
            style={{ aspectRatio: "4/5" }}
            data-testid="gallery-4"
          >
            <img src={items[9].src} alt={items[9].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-4 gallery-img"
            style={{ aspectRatio: "4/5" }}
            data-testid="gallery-5"
          >
            <img src={items[4].src} alt={items[4].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="col-span-12 md:col-span-4 gallery-img"
            style={{ aspectRatio: "4/5" }}
            data-testid="gallery-6"
          >
            <img src={items[5].src} alt={items[5].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
             transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-4 gallery-img"
            style={{ aspectRatio: "4/5" }}
            data-testid="gallery-7"
          >
            <img src={items[6].src} alt={items[6].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="col-span-6 md:col-span-6 gallery-img"
             style={{ aspectRatio: "16/10" }}
            data-testid="gallery-8"
          >
            <img src={items[7].src} alt={items[7].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="col-span-6 md:col-span-6 gallery-img"
            style={{ aspectRatio: "16/10" }}
            data-testid="gallery-9"
          >

            <img src={items[9].src} alt={items[9].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
             transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-4 gallery-img"
            style={{ aspectRatio: "4/5" }}
            data-testid="gallery-11"
          >

            <img src={items[10].src} alt={items[10].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
             transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-4 gallery-img"
            style={{ aspectRatio: "4/5" }}
            data-testid="gallery-12"
          >

            <img src={items[11].src} alt={items[11].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
             transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-4 gallery-img"
            style={{ aspectRatio: "4/5" }}
            data-testid="gallery-13"
          >

            <img src={items[12].src} alt={items[12].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="col-span-6 md:col-span-6 gallery-img"
            style={{ aspectRatio: "16/10" }}
            data-testid="gallery-14"
          >

            <img src={items[13].src} alt={items[13].cap} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="col-span-6 md:col-span-6 gallery-img"
            style={{ aspectRatio: "16/10" }}
            data-testid="gallery-15"
          >



            <img src={items[14].src} alt={items[14].cap} className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ---------------- ROOMS ----------------
const Rooms = ({ lang, onReserve }) => {
  const t = translations[lang].rooms;
  return (
    <section id="rooms" className="py-24 md:py-32" style={{ background: "var(--color-bg)" }} data-testid="rooms-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
          <div className="md:col-span-8">
            <div className="section-eyebrow">
              <span className="overline">{t.eyebrow}</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight" style={{ color: "var(--color-primary)" }} data-testid="rooms-title">
              {t.title}
            </h2>
          </div>
          <p className="md:col-span-4 text-sm" style={{ color: "var(--color-muted)" }}>
            {t.desc}
          </p>
        </div>

        <div className="border-t" style={{ borderColor: "var(--color-border)" }}>
          {t.items.map((room, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="room-row grid grid-cols-12 gap-4 py-8 md:py-10 items-center"
              data-testid={`room-${i}`}
            >
              <div className="col-span-12 md:col-span-1 font-display text-3xl" style={{ color: "var(--color-secondary)" }}>
                0{i + 1}
              </div>
              <div className="col-span-12 md:col-span-4">
                <h3 className="font-display text-2xl md:text-3xl" style={{ color: "var(--color-primary)" }}>
                  {room.name}
                </h3>
                <div className="mt-2 text-xs tracking-widest uppercase" style={{ color: "var(--color-muted)" }}>
                  {room.size} · {room.guests}
                </div>
              </div>
              <div className="col-span-12 md:col-span-4 text-sm md:text-base" style={{ color: "var(--color-muted)" }}>
                {room.view}
              </div>
              <div className="col-span-6 md:col-span-1 font-display text-2xl md:text-3xl" style={{ color: "var(--color-primary)" }}>
                {room.price}
                <span className="text-xs font-body ml-1" style={{ color: "var(--color-muted)" }}>{t.night}</span>
              </div>
              <div className="col-span-6 md:col-span-2 flex md:justify-end">
                <button
                  onClick={() => onReserve(room.name)}
                  className="btn-secondary"
                  style={{ padding: "0.6rem 1.2rem", fontSize: "0.72rem" }}
                  data-testid={`room-reserve-${i}`}
                >
                  {t.reserve}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------- ACTIVITIES ----------------
const Activities = ({ lang }) => {
  const t = translations[lang].activities;
  const images = [MEDIA.SwimmingPool, MEDIA.snow, MEDIA.bedroom1, MEDIA.fireplace];
  return (
    <section id="activities" className="py-24 md:py-32" style={{ background: "var(--color-primary)", color: "#fff" }} data-testid="activities-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="section-eyebrow">
          <span className="overline" style={{ color: "#E5C9A8" }}>{t.eyebrow}</span>
        </div>
        <h2 className="font-display text-4xl md:text-6xl max-w-2xl mb-16 leading-[1.05]" data-testid="activities-title">
          {t.title}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          {t.items.map((a, i) => {
            const Icon = activityIcons[i] || Tree;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                className="grid grid-cols-12 gap-6 items-start"
                data-testid={`activity-${i}`}
              >
                <div className="col-span-12 md:col-span-5 gallery-img" style={{ aspectRatio: "4/3" }}>
                  <img src={images[i]} alt={a.t} className="w-full h-full object-cover" />
                </div>
                <div className="col-span-12 md:col-span-7">
                  <Icon size={28} weight="duotone" color="#E5C9A8" />
                  <h3 className="font-display text-3xl mt-4">{a.t}</h3>
                  <p className="mt-3 opacity-80 leading-relaxed">{a.d}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ---------------- REVIEWS ----------------
const Reviews = ({ lang }) => {
  const t = translations[lang].reviews;
  return (
    <section className="py-24 md:py-32" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="section-eyebrow">
          <span className="overline">{t.eyebrow}</span>
        </div>
        <h2 className="font-display text-4xl md:text-5xl mb-14 max-w-2xl" style={{ color: "var(--color-primary)" }} data-testid="reviews-title">
          {t.title}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {t.items.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="review-card"
              data-testid={`review-${i}`}
            >
              <Quotes size={32} weight="fill" color="#A35D43" />
              <p className="font-display italic text-2xl mt-6 leading-snug" style={{ color: "var(--color-primary)" }}>
                "{r.q}"
              </p>
              <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--color-border)" }}>
                <div className="font-medium" style={{ color: "var(--color-primary)" }}>{r.a}</div>
                <div className="overline mt-1">{r.loc}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------- CONTACT / BOOKING FORM ----------------
const Contact = ({ lang, formData, setFormData }) => {
  const t = translations[lang].contact;
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        language: lang,
      });
      setStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        check_in: "",
        check_out: "",
        guests: 2,
        room: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        setErrorMsg(typeof detail === "string" ? detail : (lang === "AL" ? "Datat janë të zëna. Ju lutem zgjidhni datat tjera." : "Those dates are not available."));
      } else {
        setErrorMsg(typeof detail === "string" ? detail : t.error);
      }
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (k) => (e) => setFormData({ ...formData, [k]: e.target.value });

  return (
    <section id="contact" className="py-24 md:py-32" style={{ background: "var(--color-surface)" }} data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <div className="section-eyebrow">
              <span className="overline">{t.eyebrow}</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05]" style={{ color: "var(--color-primary)" }} data-testid="contact-title">
              {t.title}
            </h2>
            <p className="mt-6 text-lg" style={{ color: "var(--color-muted)" }}>
              {t.desc}
            </p>

            <div className="mt-12 space-y-5">
              <a href="mailto:hello@villadelight.com" className="flex items-center gap-3 nav-link" data-testid="contact-email-link">
                <EnvelopeSimple size={18} />
                info@villadelightks.com
              </a>
              <a href="tel:+38346323231" className="flex items-center gap-3 nav-link" data-testid="contact-phone-link">
                <Phone size={18} />
                +383 46 323 231
              </a>
              <a href="https://wa.me/38346323231" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 nav-link" data-testid="contact-whatsapp-link">
                <Phone size={18} weight="fill" />
                WhatsApp
              </a>
              <a href="viber://chat?number=+38346323231" className="flex items-center gap-3 nav-link" data-testid="contact-viber-link">
                <Phone size={18} weight="fill" />
                Viber
              </a>
<div className="flex items-center gap-3" style={{ color: "var(--color-muted)" }}>
  <MapPin size={18} />
  <a
    href="https://maps.app.goo.gl/m8X2n568NbFKtobZ7"
    target="_blank"
    rel="noopener noreferrer"
    className="hover:underline"
  >
    {lang === "EN" ? "Jezerc · Ferizaj, Kosovo" : "Jezerc · Ferizaj, Kosovë"}
  </a>
</div>
              <a href={PAYPAL_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-paypal mt-10 w-full justify-center" data-testid="contact-pay-btn">
              {translations[lang].hero.pay}
              <ArrowUpRight size={16} />
              </a>
              </div>
            </div>

          <form onSubmit={handleSubmit} className="md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
            {formData.room && (
              <div className="md:col-span-2 px-4 py-3 flex items-center justify-between" style={{ background: "var(--color-bg)", borderLeft: "3px solid var(--color-secondary)" }} data-testid="contact-room-chip">
                <span className="text-sm" style={{ color: "var(--color-primary)" }}>
                  <span className="overline mr-2" style={{ color: "var(--color-secondary)" }}>{lang === "EN" ? "Room" : "Dhoma"}</span>
                  <span className="font-display text-lg">{formData.room}</span>
                </span>
                <button type="button" onClick={() => setFormData({ ...formData, room: "" })} className="text-xs opacity-60 hover:opacity-100" data-testid="contact-room-clear" style={{ color: "var(--color-primary)" }}>×</button>
              </div>
            )}
            <div className="md:col-span-2">
              <label className="overline block mb-2">{t.name}</label>
              <input
                required
                value={formData.name}
                onChange={update("name")}
                className="w-full bg-transparent border-0 border-b-2 focus:outline-none py-3 text-lg"
                style={{ borderColor: "var(--color-primary)", color: "var(--color-primary)" }}
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="overline block mb-2">{t.email}</label>
              <input
                required
                type="email"
                value={formData.email}
                onChange={update("email")}
                className="w-full bg-transparent border-0 border-b-2 focus:outline-none py-3 text-lg"
                style={{ borderColor: "var(--color-primary)", color: "var(--color-primary)" }}
                data-testid="contact-email"
              />
            </div>
            <div>
              <label className="overline block mb-2">{t.phone}</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={update("phone")}
                className="w-full bg-transparent border-0 border-b-2 focus:outline-none py-3 text-lg"
                style={{ borderColor: "var(--color-primary)", color: "var(--color-primary)" }}
                data-testid="contact-phone"
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-3">{translations[lang].booking.checkin} / {translations[lang].booking.checkout}</label>
              <DateRangePicker
                lang={lang}
                checkIn={formData.check_in}
                checkOut={formData.check_out}
                onChange={({ check_in, check_out }) => setFormData({ ...formData, check_in, check_out })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">{t.message}</label>
              <textarea
                value={formData.message}
                onChange={update("message")}
                rows={4}
                className="w-full bg-transparent border-0 border-b-2 focus:outline-none py-3 text-base resize-none"
                style={{ borderColor: "var(--color-primary)", color: "var(--color-primary)" }}
                data-testid="contact-message"
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-6 flex-wrap mt-4">
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary"
                data-testid="contact-submit-btn"
              >
                {submitting ? t.sending : t.send}
                {!submitting && <ArrowUpRight size={16} />}
              </button>
              {status === "success" && (
                <span className="text-sm" style={{ color: "var(--color-primary)" }} data-testid="contact-success">
                  ✓ {t.success}
                </span>
              )}
              {status === "error" && (
                <span className="text-sm text-red-700" data-testid="contact-error">
                {errorMsg || t.error}
                </span>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

// ---------------- FOOTER ----------------
const Footer = ({ lang }) => {
  const t = translations[lang].footer;
  return (
    <footer className="pt-24 pb-10" style={{ background: "var(--color-primary)", color: "#fff" }} data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="footer-giant leading-none" data-testid="footer-giant">Villa Delight.</div>
        <p className="mt-6 font-display italic text-2xl opacity-80">{t.tag}</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mt-20 pt-12 border-t border-white/20">
          <div>
            <div className="overline" style={{ color: "#E5C9A8" }}>{t.visit}</div>
            <p className="mt-3 whitespace-pre-line opacity-80 text-sm leading-relaxed">{t.address}</p>
          </div>
          <div>
            <div className="overline" style={{ color: "#E5C9A8" }}>{t.contact}</div>
            <a href={`mailto:${t.email}`} className="block mt-3 opacity-80 hover:opacity-100 text-sm" data-testid="footer-email">{t.email}</a>
            <a href={`tel:${t.phone.replace(/\s/g,'')}`} className="block mt-1 opacity-80 hover:opacity-100 text-sm" data-testid="footer-phone">{t.phone}</a>
          </div>
          <div>
            <div className="overline" style={{ color: "#E5C9A8" }}>{t.follow}</div>
            <div className="flex gap-4 mt-3">
              <a href="https://www.instagram.com/villadelight.ks" className="opacity-80 hover:opacity-100" data-testid="footer-instagram"><InstagramLogo size={22} /></a>
              <a href="#" className="opacity-80 hover:opacity-100" data-testid="footer-facebook"><FacebookLogo size={22} /></a>
            </div>
          </div>
          <div className="text-right">
            <div className="overline" style={{ color: "#E5C9A8" }}>EST.</div>
            <div className="font-display text-3xl mt-3">MMXVIII</div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs opacity-70">
          <span>{t.rights}</span>
          <span className="font-display italic">{t.crafted}</span>
        </div>
      </div>
    </footer>
  );
};

// ---------------- MAIN APP ----------------
function App() {
  const [lang, setLang] = useState("AL");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    check_in: "",
    check_out: "",
    guests: 2,
    room: "",
    message: "",
  });

  useEffect(() => {
    axios.get(`${API}/`).catch((e) => console.log("API check failed", e?.message));
  }, []);

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleHeroContinue = () => {
    scrollToContact();
  };

  const handleRoomReserve = (roomName) => {
    setFormData((prev) => ({ ...prev, room: roomName }));
    scrollToContact();
  };

  return (
    <div className="App" data-testid="app-root">
      <Header lang={lang} setLang={setLang} onBook={scrollToContact} />
      <Hero lang={lang} formData={formData} setFormData={setFormData} onContinue={handleHeroContinue} />
      <MarqueeStrip />
      <About lang={lang} />
      <Amenities lang={lang} />
      <Gallery lang={lang} />
      <Rooms lang={lang} onReserve={handleRoomReserve} />
      <Activities lang={lang} />
      <Reviews lang={lang} />
      <Contact lang={lang} formData={formData} setFormData={setFormData} />
      <Footer lang={lang} />
    </div>
  );
}

export default App;
